import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import * as React from 'react';
import cx from 'classnames';
import { AnchorLink } from '../shared/anchor-link';
import { useIntl } from 'gatsby-plugin-react-intl';

interface AuthorProps {
  teamMember?: {
    name: string;
    role: string;
    pathname?: string;
    slug?: string;
    photo?: {
      localFile?: {
        childImageSharp?: {
          gatsbyImageData: IGatsbyImageData;
        };
      };
    };
    categories?: Array<{
      label: string;
      value: string;
    }>;
  };
  author?: string;
  size?: 'small' | 'large';
  isConsultant?: boolean
  className?: string;
}

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const Author: React.FC<AuthorProps> = ({
  teamMember,
  author,
  size = 'large',
  isConsultant,
  className,
}) => {
  const { locale } = useIntl();

  // Don't render if no author data is available
  if (!teamMember && !author) {
    return null;
  }

  const displayName = teamMember?.name || author;
  const displayRole = isConsultant
    ? locale === "pl" ? 'Konsultacja merytoryczna' : 'Substantive consultation'
    : teamMember?.role;
  const categories = teamMember?.categories || [];
  const photo = teamMember?.photo?.localFile?.childImageSharp?.gatsbyImageData;
  const initials = displayName ? getInitials(displayName) : '';
  const teamMemberLink = teamMember?.pathname && teamMember?.slug 
    ? `/${teamMember.pathname}/${teamMember.slug}` 
    : null;

  const isSmall = size === 'small';

  return (
    <div className={cx('flex items-center space-x-3 w-full', className)}>
      {/* Avatar */}
      <div
        className={cx(
          'flex-shrink-0 rounded-full overflow-hidden',
          isSmall ? 'w-10 h-10' : 'w-24 h-24'
        )}
      >
        {photo ? (
          <GatsbyImage
            image={photo}
            alt={displayName || ''}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className={cx(
              'w-full h-full bg-gray-300 flex items-center justify-center text-gray-600 font-medium',
              isSmall ? 'text-xs' : 'text-sm'
            )}
          >
            {initials}
          </div>
        )}
      </div>

      {/* Text */}
      <div className="min-w-0 text-left flex-1">
        {teamMemberLink ? (
          <AnchorLink
            to={teamMemberLink}
            className={cx(
              'font-medium text-gray-900 truncate hover:text-green-600 transition-colors',
              isSmall ? 'text-sm' : 'text-base'
            )}
          >
            {displayName}
          </AnchorLink>
        ) : (
          <p
            className={cx(
              'font-medium text-gray-900 truncate',
              isSmall ? 'text-sm' : 'text-base'
            )}
          >
            {displayName}
          </p>
        )}
        {displayRole && (
          <p
            className={cx(
              'text-gray-500 truncate max-w-fit',
              isSmall ? 'text-xs' : 'text-sm'
            )}
          >
            {displayRole}
          </p>
        )}

        {/* Categories badges - only show for large size */}
        {!isSmall && categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {categories.map((category, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
              >
                {category.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
